import type { PaymentGateway } from '../../domain/PaymentsAccount'
import type { PaymentIntent } from '../../domain/PaymentIntent'
import {
    PaymentSession,
    PaymentSessionOptions,
} from '../PaymentSession'
import {
    DojoCard,
    DojoCardErrorMessages,
    DojoCardStylesConfiguration,
    DojoCardTextConfiguration,
    DojoClient,
    DojoPaymentResult,
    DojoValidationError,
    DojoWallet,
} from './dojo-client'

const DOJO_CARD_FONT_URL = 'https://fonts.googleapis.com/css?family=Inter:400,500,600&display=swap'
const DOJO_CARD_FIELD_STYLES = {
    appearance: 'none',
    backgroundColor: '#FFFFFF',
    border: '1px solid #F1F1F1',
    borderRadius: '10px',
    boxSizing: 'border-box',
    boxShadow: 'none',
    color: '#0D0D0D',
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    fontWeight: '400',
    height: '48px',
    lineHeight: '24px',
    padding: '11px 13px',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    WebkitAppearance: 'none',
    width: '100%',
}
const DOJO_CARD_STYLES: DojoCardStylesConfiguration = {
    base: {
        default: DOJO_CARD_FIELD_STYLES,
        focus: {
            border: '1px solid #158849',
            boxShadow: '0 0 0 3px #99D3AD',
            outline: 'none',
        },
        error: {
            border: '1px solid #CC2937',
        },
        errorFocus: {
            border: '1px solid #CC2937',
            boxShadow: '0 0 0 3px #E9989D',
            outline: 'none',
        },
        container: {
            position: 'relative',
        },
        label: {
            color: '#0D0D0D',
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '20px',
            marginBottom: '8px',
        },
        validationText: {
            color: '#CC2937',
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px',
            fontWeight: '500',
            left: 'auto',
            lineHeight: '20px',
            margin: '0',
            maxWidth: '50%',
            overflow: 'hidden',
            padding: '0',
            position: 'absolute',
            right: '0',
            textAlign: 'right',
            textOverflow: 'ellipsis',
            top: '0',
            whiteSpace: 'nowrap',
            zIndex: '1',
        },
        validationIcon: {
            display: 'none',
        },
    },
    cardName: {
        container: { gridColumn: '1 / -1' },
    },
    cardNumber: {
        container: { gridColumn: '1 / -1' },
    },
    cardIcon: {
        bottom: '17px',
        position: 'absolute',
        right: '12px',
    },
    expiryDate: {
        container: { minWidth: '0' },
    },
    cv2: {
        container: { minWidth: '0' },
    },
    form: {
        backgroundColor: 'transparent',
        border: '0',
        borderRadius: '0',
        boxShadow: 'none',
        boxSizing: 'border-box',
        columnGap: '12px',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        padding: '0 3px 0',
        rowGap: '16px',
        width: '100%',
    },
}
const DOJO_CARD_TEXT: DojoCardTextConfiguration = {
    cardName: { label: 'Name on card', placeholder: 'Full name', showValidation: true },
    cardNumber: {
        label: 'Card number',
        placeholder: '1234 1234 1234 1234',
        showValidation: true,
    },
    expiryDate: { label: 'Expiry date', placeholder: 'MM / YY', showValidation: true },
    cv2: { label: 'Security code', placeholder: 'CVC', showValidation: true },
}
const DOJO_CARD_ERROR_MESSAGES: DojoCardErrorMessages = {
    cardNameRequired: 'Required',
    cardNameInvalid: 'Invalid',
    cardNumberRequired: 'Required',
    cardNumberInvalid: 'Invalid',
    expiryDateRequired: 'Required',
    expiryDateInvalid: 'Invalid',
    expiryDateMustBeInFuture: 'Expired',
    cv2Required: 'Required',
    cv2Invalid: 'Invalid',
}
let nextDojoSessionId = 0

function isDojoPaymentResult(value: unknown): value is DojoPaymentResult {
    return typeof value === 'object'
        && value !== null
        && 'statusCode' in value
        && typeof value.statusCode === 'number'
}

export class DojoSession implements PaymentSession {

    readonly gateway: PaymentGateway = 'dojo'
    readonly walletPaymentAvailable: boolean

    private readonly card: DojoCard
    private readonly wallet: DojoWallet | null
    private readonly ownedContainers: HTMLElement[] = []
    private readonly ownerId = `dojo-session-${++nextDojoSessionId}`
    private validityHandler: (isValid: boolean) => void = () => undefined
    private walletHandler: (intentId: string) => void = () => undefined
    private errorHandler: (message: string) => void = () => undefined
    private walletConfirmedIntentId: string | null = null
    private needsRefresh = false
    private refreshing: Promise<void> | null = null
    private destroyed = false

    private constructor(
        dojo: DojoClient,
        document: Document,
        private readonly options: PaymentSessionOptions,
        private readonly refreshIntent: () => Promise<PaymentIntent>,
        iframeLoaded: () => void,
        iframeFailed: (error: Error) => void
    ) {
        const cardContainer = this.ownContainer(document, options.cardFormSelector)
        this.card = new dojo.Payment(
            {
                containerId: cardContainer.id,
                paymentDetails: this.paymentDetails(),
                fontCss: [DOJO_CARD_FONT_URL],
                styles: DOJO_CARD_STYLES,
                text: DOJO_CARD_TEXT,
                errorMessages: DOJO_CARD_ERROR_MESSAGES,
                callbacks: {
                    onFormComplete: isComplete => {
                        if (!this.destroyed) {
                            this.validityHandler(isComplete ?? true)
                        }
                    },
                },
                onIframeLoaded: iframeLoaded,
                onIframeLoadFailed: () => {
                    iframeFailed(new Error(
                        'Unable to load the secure Dojo card form. Please refresh the page and try again.'
                    ))
                },
            },
            errors => this.handleCardValidationErrors(errors),
            () => undefined,
            isValid => {
                if (!this.destroyed) {
                    this.validityHandler(isValid)
                }
            }
        )

        const walletSelector = options.applePayWalletButtonSelector ?? options.googlePayWalletButtonSelector
        if (walletSelector) {
            const walletContainer = this.ownContainer(document, walletSelector)
            this.wallet = new dojo.WalletPayment(
                {
                    containerId: walletContainer.id,
                    paymentDetails: this.paymentDetails(),
                    buttonConfig: { color: 'black', type: 'plain', sizeMode: 'fill' },
                    emailRequired: true,
                    billingAddressRequired: false,
                    shippingAddressRequired: false,
                },
                errors => this.handleWalletErrors(errors),
                result => this.handleWalletResult(result)
            )
        } else {
            this.wallet = null
        }
        this.walletPaymentAvailable = this.wallet !== null
    }

    static create(
        dojo: DojoClient,
        document: Document,
        options: PaymentSessionOptions,
        refreshIntent: () => Promise<PaymentIntent>
    ): Promise<DojoSession> {
        if (!options.intent.clientSecret.trim()) {
            return Promise.reject(new Error('Dojo client session secret is required'))
        }
        return new Promise<DojoSession>((resolve, reject) => {
            let session: DojoSession | null = null
            let settled = false
            let iframeDidLoad = false
            const loaded = () => {
                iframeDidLoad = true
                if (!settled && session) {
                    settled = true
                    resolve(session)
                }
            }
            const failed = (error: Error) => {
                if (!settled) {
                    settled = true
                    session?.destroy()
                    reject(error)
                }
            }
            try {
                session = new DojoSession(dojo, document, options, refreshIntent, loaded, failed)
                if (iframeDidLoad && !settled) {
                    settled = true
                    resolve(session)
                }
            } catch (error) {
                failed(error instanceof Error ? error : new Error(String(error)))
            }
        })
    }

    setOnValidityChange(handler: (isValid: boolean) => void): void {
        this.validityHandler = handler
    }

    setOnWalletConfirmed(handler: (intentId: string) => void): void {
        this.walletHandler = handler
    }

    setOnError(handler: (message: string) => void): void {
        this.errorHandler = handler
    }

    async confirm(billingEmailAddress?: string): Promise<string> {
        if (this.walletConfirmedIntentId !== null) {
            return this.walletConfirmedIntentId
        }
        if (this.destroyed) {
            throw new Error('Payment session is no longer active.')
        }
        if (this.needsRefresh) {
            await this.refreshSession()
            if (this.destroyed) {
                throw new Error('Payment session is no longer active.')
            }
        }
        const email = (
            billingEmailAddress
            ?? this.options.getBillingEmailAddress?.()
        )?.trim()
        let result: DojoPaymentResult
        try {
            result = await this.card.executePayment(
                email ? { userEmailAddress: email } : undefined
            )
        } catch (error) {
            if (isDojoPaymentResult(error)) {
                return this.complete(error)
            }
            throw error
        }
        return this.complete(result)
    }

    destroy(): void {
        this.destroyed = true
        this.ownedContainers.forEach(container => {
            if (container.dataset['dojoSessionOwner'] === this.ownerId) {
                container.replaceChildren()
                delete container.dataset['dojoSessionOwner']
            }
        })
        this.ownedContainers.length = 0
    }

    private ownContainer(document: Document, selector: string): HTMLElement {
        const container = document.querySelector<HTMLElement>(selector)
        if (!container) {
            throw new Error(`Dojo payment container was not found: ${selector}`)
        }
        if (!container.id) {
            container.id = `${this.ownerId}-${this.ownedContainers.length + 1}`
        }
        container.replaceChildren()
        container.dataset['dojoSessionOwner'] = this.ownerId
        this.ownedContainers.push(container)
        return container
    }

    private paymentDetails() {
        return { paymentToken: this.options.intent.clientSecret }
    }

    private async complete(result: DojoPaymentResult): Promise<string> {
        if (result.statusCode === 0 || result.statusCode === 500) {
            // Status 500 is ambiguous, so let the backend verify the known intent.
            return this.options.intent.id
        }
        const providerMessage = typeof result.message === 'string' ? result.message.trim() : ''
        const message = providerMessage
            || 'Your payment could not be completed. Please check your card details and try again.'
        if ([5, 30, 400, 401, 404].includes(result.statusCode)) {
            this.needsRefresh = true
            try {
                await this.refreshSession()
            } catch {
                // Keep the decline visible; the next confirmation retries the refresh first.
            }
        }
        if (!this.destroyed) {
            this.errorHandler(message)
        }
        throw new Error(message)
    }

    private refreshSession(): Promise<void> {
        if (!this.refreshing) {
            this.refreshing = this.refreshIntent().then(intent => {
                if (this.destroyed) {
                    return
                }
                if (intent.id !== this.options.intent.id || !intent.clientSecret?.trim()) {
                    throw new Error('Unable to refresh the payment session. Please try again.')
                }
                const details = { paymentToken: intent.clientSecret }
                this.card.updateAccessToken(details)
                this.wallet?.updateAccessToken(details)
                this.needsRefresh = false
            }).finally(() => {
                this.refreshing = null
            })
        }
        return this.refreshing
    }

    private handleWalletResult(result: DojoPaymentResult): void {
        if (this.destroyed) {
            return
        }
        void this.complete(result).then(intentId => {
            if (this.destroyed) {
                return
            }
            this.walletConfirmedIntentId = intentId
            this.walletHandler(intentId)
        }).catch(() => {
            // complete() has already surfaced the recoverable error.
        })
    }

    private handleWalletErrors(errors: DojoValidationError[]): void {
        if (this.destroyed || errors.length === 0) {
            return
        }
        this.validityHandler(false)
        const message = errors
            .map(error => error.message?.trim())
            .filter((value): value is string => Boolean(value))
            .join(' ')
        if (message) {
            this.errorHandler(message)
        }
    }

    private handleCardValidationErrors(errors: DojoValidationError[]): void {
        if (!this.destroyed && errors.length > 0) {
            this.validityHandler(false)
        }
    }

}
